/**
 * Typed wrapper around clipper2-wasm (spike winner — docs/bim-plan/04 §4 Q4).
 * ALL embind memory management (`.delete()`) is confined to this file; everything else in
 * the package works with plain Pt2[][] in FEET. Internal integer scale: 1 ft = 10,000
 * units (0.1 mil precision; a 1,000 ft site squares to 1e14, well inside 2^53).
 */
import factory from 'clipper2-wasm/dist/es/clipper2z.js'
import type { Pt2 } from '@aj/bim-kernel'

const SCALE = 10_000

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let M: any = null
let initPromise: Promise<void> | null = null

export interface ClipperInitOptions {
  /** Vite path: pass the `?url` asset for clipper2z.wasm. Node/vitest: omit (emscripten resolves it). */
  wasmUrl?: string
}

export function initClipper(opts: ClipperInitOptions = {}): Promise<void> {
  if (!initPromise) {
    initPromise = factory(
      opts.wasmUrl ? { locateFile: () => opts.wasmUrl! } : undefined,
    ).then((mod) => { M = mod })
  }
  return initPromise
}

export const clipperReady = (): boolean => M !== null

function assertReady(): void {
  if (!M) throw new Error('clipper not initialized — await initClipper() first')
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toPaths64(paths: Pt2[][]): any {
  const ps = new M.Paths64()
  for (const path of paths) {
    const flat: number[] = []
    for (const [x, y] of path) flat.push(Math.round(x * SCALE), Math.round(y * SCALE))
    const p = M.MakePath64(flat)
    ps.push_back(p)
    p.delete()
  }
  return ps
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function fromPaths64(ps: any): Pt2[][] {
  const out: Pt2[][] = []
  for (let i = 0; i < ps.size(); i++) {
    const p = ps.get(i)
    const path: Pt2[] = []
    for (let j = 0; j < p.size(); j++) {
      const pt = p.get(j)
      path.push([Number(pt.x) / SCALE, Number(pt.y) / SCALE])
    }
    out.push(path)
  }
  return out
}

/**
 * Inflate an OPEN polyline both sides by `deltaFt` (canonical Clipper2 semantics: the
 * result is 2×delta wide) with miter joins and butt ends — the wall centerline → outline
 * operation. Returns closed outline path(s); self-touching arms merge (spike-verified).
 */
export function inflateOpen(centerline: Pt2[], deltaFt: number, miterLimit = 2): Pt2[][] {
  assertReady()
  const subject = toPaths64([centerline])
  const result = M.InflatePaths64(subject, deltaFt * SCALE, M.JoinType.Miter, M.EndType.Butt, miterLimit, 2)
  const out = fromPaths64(result)
  subject.delete(); result.delete()
  return out
}

/**
 * Inflate a CLOSED ring as a polygon boundary by `deltaFt` (positive = outward for a
 * CCW ring, negative = inward) with miter joins — the closed-loop wall operation
 * (2.6 polish, Matt's corner-notch report): a wall centerline that closes on itself is
 * offset as a true ring, so EVERY corner miters, including the drawing seam that open
 * inflation leaves as two butt caps. Callers normalize the ring CCW first.
 */
export function inflateClosedRing(ring: Pt2[], deltaFt: number, miterLimit = 2): Pt2[][] {
  assertReady()
  const subject = toPaths64([ring])
  const result = M.InflatePaths64(subject, deltaFt * SCALE, M.JoinType.Miter, M.EndType.Polygon, miterLimit, 2)
  const out = fromPaths64(result)
  subject.delete(); result.delete()
  return out
}

/**
 * Inflate a polygon SET (outer CCW + holes CW, orientation kept) by `deltaFt` with
 * miter joins — positive grows outers and shrinks holes. CEILING-BY-ROOM round.
 */
export function inflateClosed(paths: Pt2[][], deltaFt: number, miterLimit = 4): Pt2[][] {
  assertReady()
  if (paths.length === 0) return []
  const subject = toPaths64(paths)
  const result = M.InflatePaths64(subject, deltaFt * SCALE, M.JoinType.Miter, M.EndType.Polygon, miterLimit, 2)
  const out = fromPaths64(result)
  subject.delete(); result.delete()
  return out
}

/**
 * Union of closed polygons that also CLOSES gaps narrower than 2×`gapFt` between them
 * (grow, merge, shrink back — a morphological closing with square corners kept by the
 * miter joins). SPLIT-BAND strips: a wall face cut around an opening comes back as
 * patches a hair apart, and a plain union leaves them as separate strips with walls in
 * between. Input polygons may wind either way (each is treated as a solid).
 */
export function unionClosedGaps(paths: Pt2[][], gapFt: number): Pt2[][] {
  assertReady()
  if (paths.length === 0) return []
  const ccw = paths.map((p) => (signedArea(p) < 0 ? [...p].reverse() : p))
  const subject = toPaths64(ccw)
  const grown = M.InflatePaths64(subject, gapFt * SCALE, M.JoinType.Miter, M.EndType.Polygon, 4, 2)
  const shrunk = M.InflatePaths64(grown, -gapFt * SCALE, M.JoinType.Miter, M.EndType.Polygon, 4, 2)
  const out = fromPaths64(shrunk)
  subject.delete(); grown.delete(); shrunk.delete()
  return out
}

/** NonZero union of closed paths — the wall-join operation (same-assembly runs merge). */
export function unionClosed(paths: Pt2[][]): Pt2[][] {
  assertReady()
  if (paths.length === 0) return []
  const subject = toPaths64(paths)
  const empty = new M.Paths64()
  const result = M.Union64(subject, empty, M.FillRule.NonZero)
  const out = fromPaths64(result)
  subject.delete(); empty.delete(); result.delete()
  return out
}

/** NonZero intersection (subject ∩ clip) — the r14b 3D cutaway region clip. */
export function intersectClosed(subject: Pt2[][], clip: Pt2[][]): Pt2[][] {
  assertReady()
  if (subject.length === 0 || clip.length === 0) return []
  const s = toPaths64(subject)
  const c = toPaths64(clip)
  const result = M.Intersect64(s, c, M.FillRule.NonZero)
  const out = fromPaths64(result)
  s.delete(); c.delete(); result.delete()
  return out
}

/** NonZero difference (subject minus clip) — opening subtraction in Phase 2. */
export function differenceClosed(subject: Pt2[][], clip: Pt2[][]): Pt2[][] {
  assertReady()
  if (subject.length === 0) return []
  if (clip.length === 0) return subject.map((p) => p.map((q) => [...q] as Pt2))
  const s = toPaths64(subject)
  const c = toPaths64(clip)
  const result = M.Difference64(s, c, M.FillRule.NonZero)
  const out = fromPaths64(result)
  s.delete(); c.delete(); result.delete()
  return out
}

/**
 * Signed shoelace area in SF (plain JS — no wasm round-trip needed).
 * Convention: POSITIVE = counter-clockwise in our Y-up world. This sign is load-bearing:
 * extrude.ts normalizes ring order with it, and triangle winding (which drives backface
 * culling) follows ring order. A flipped convention here inverted every wall's winding —
 * front faces were culled and only back sides rendered (Matt's report, 2026-07-16).
 */
export function signedArea(path: Pt2[]): number {
  let s = 0
  for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
    s += path[j][0] * path[i][1] - path[i][0] * path[j][1]
  }
  return s / 2
}
